/**
 * Record a CLIENT-side error boundary hit (#874).
 *
 * The third and last sink in the set. `instrumentation.ts` captures what
 * escapes to Next on the server; `logCaughtServerError` (#660) captures what
 * server code deliberately swallows. Neither can see a crash that happens in
 * the browser — and until this existed, an `error.tsx` reported to Sentry and
 * to nothing else, so a client crash left no trace in any log the project
 * owns: not `server_errors`, not Postgres, not Vercel (free-tier runtime logs
 * are live-tail only).
 *
 * That cost a whole round of forensics on #864. `server_errors`, the Postgres
 * logs, `recipe_translations.updated_at` and an en→de key diff all came back
 * clean — correctly, because the crash never reached the server — and the bug
 * was only solvable once Sentry credentials happened to land in the session
 * environment. Sentry stays the richer record (breadcrumbs, releases, source
 * maps); this makes the bare fact of the crash reachable by SQL alone.
 *
 * Best-effort in every direction: called from a boundary that is already
 * rendering a broken page, so it must never throw and never block the render.
 */

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * A Server Action cannot be the target of a beacon, and a boundary fires
 * exactly when the React/action runtime may be unusable — so the ingest is a
 * route handler, on the same grounds as the Web Share Target (CLAUDE.md §6).
 */
const INGEST_PATH = '/api/client-errors';

const MAX_MESSAGE_CHARS = 1000;
const MAX_STACK_CHARS = 8000;
const MAX_WHERE_CHARS = 100;

export interface BoundaryError {
  message?: string;
  stack?: string;
  digest?: string;
}

/**
 * @param where Stable boundary identifier, e.g. `recipes/[slug]`. Lands in
 *              `server_errors.render_source`, so keep it greppable.
 */
export function reportBoundaryError(where: string, error: BoundaryError | null | undefined): void {
  try {
    Sentry.captureException(error);
  } catch {
    // never let error reporting throw
  }

  try {
    const body = JSON.stringify({
      where: where.slice(0, MAX_WHERE_CHARS),
      message: (error?.message ?? String(error)).slice(0, MAX_MESSAGE_CHARS),
      stack: error?.stack?.slice(0, MAX_STACK_CHARS),
      digest: error?.digest,
      // Path + query only. Never the hash, which is client-only state and the
      // likeliest place for something we shouldn't persist.
      url: `${window.location.pathname}${window.location.search}`,
    });

    // `keepalive` so the report survives the user closing a broken tab.
    void fetch(INGEST_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      // The endpoint is same-origin, which `connect-src 'self'` allows (§11).
      credentials: 'omit',
    }).catch(() => {
      // A FAILED BEACON IS NEVER ITSELF REPORTED. Routing it back through
      // Sentry or through this function would make a broken ingest endpoint
      // report itself for as long as the page is open.
    });
  } catch {
    // Serialising or dispatching the report may not break the boundary.
  }
}

/**
 * The one line every `error.tsx` runs. Enforced by
 * `npm run lint:boundary-reporting` — a boundary that reports to Sentry
 * directly is a boundary that is invisible to SQL, which is the whole defect
 * this ticket fixed.
 */
export function useBoundaryErrorReport(
  where: string,
  error: BoundaryError | null | undefined,
): void {
  useEffect(() => {
    reportBoundaryError(where, error);
  }, [where, error]);
}
